<template>
    <div class="flex flex-column justify-content-center align-items-center w-full h-full">
        <Form class="form">
            <div class=" w-full">
                <div class="flex flex-column col-12 justify-content-start gap-2 pt-4">
                    <h4 class="Paragraph-Medium text-left">Education Qualifications</h4>

                    <div class="grid w-full align-items-center justify-content-between pt-4 relative mb-5">
                        <div class="col-12 md:col-6 flex align-items-center">
                            <label class="Label text-left" for="prfPhoto">Professional photo:</label>
                        </div>
                        <div class="col-12 md:col-6 flex justify-content-end p-0 ">
                            <FileUpload mode="basic" @select="uploadPrfPhoto" customUpload auto
                                :severity="prfPhoto ? 'success' : 'secondary'" class="teal-button-transparent"
                                :chooseLabel="prfPhoto ? 'Uploaded' : 'Upload'"
                                :chooseIcon="prfPhoto ? 'pi pi-check' : 'pi pi-upload'" />
                        </div>
                        <small v-if="errors.prfPhoto" class="error-text" style="right: 0; left: auto;">{{
                            errors.prfPhoto }}</small>
                    </div>

                    <div class="grid w-full align-items-center justify-content-between pt-4 relative mb-5">
                        <div class="col-12 md:col-6 flex align-items-center">
                            <label class="Label text-left" for="nic">NIC:</label>
                        </div>
                        <div class="col-12 md:col-6 flex justify-content-end p-0">
                            <FileUpload mode="basic" @select="uploadNIC" customUpload auto
                                :severity="nic ? 'success' : 'secondary'" class="teal-button-transparent"
                                :chooseLabel="nic ? 'Uploaded' : 'Upload'"
                                :chooseIcon="nic ? 'pi pi-check' : 'pi pi-upload'" />
                        </div>
                        <small v-if="errors.nic" class="error-text" style="right: 0; left: auto;">{{ errors.nic
                            }}</small>
                    </div>

                    <div class="grid w-full align-items-center justify-content-between pt-4 relative mb-5">
                        <div class="col-12 md:col-6 flex align-items-center">
                            <label class="Label text-left" for="drivingLicence">Driving License (Optional):</label>
                        </div>
                        <div class="col-12 md:col-6 flex justify-content-end p-0">
                            <FileUpload mode="basic" @select="uploadLicense" customUpload auto
                                :severity="drivingLicence ? 'success' : 'secondary'" class="teal-button-transparent"
                                :chooseLabel="drivingLicence ? 'Uploaded' : 'Upload'"
                                :chooseIcon="drivingLicence ? 'pi pi-check' : 'pi pi-upload'" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="w-full flex justify-content-between mt-6">
                <Button label="Back" class="teal-button-transparent" @click="Cancel" />
                <Button label="Next" class="teal-button" @click="Next" />
            </div>

        </Form>

    </div>
</template>

<script setup>
import { ref, defineEmits, watch, onMounted } from "vue";
import { Form } from "@primevue/forms";
import Button from "primevue/button";
import FileUpload from 'primevue/fileupload';
import { uploadIdentification } from "@/api/storage";
import { notifyError, notifyInfo } from "@/utils/notification";
import { useAuthStore } from "@/stores/authStore";

const authStore = useAuthStore();

onMounted(() => {
    const user = authStore.user;
    if (user) {
        prfPhoto.value = user.professionalPhotoURL || null;
        nic.value = user.NICURL || null;
        drivingLicence.value = user.drivingLicenseURL || null;
    }
});

const emit = defineEmits(["submit-verification-info", "previous-step"]);

const errors = ref({});
const prfPhoto = ref(null);
const nic = ref(null);
const drivingLicence = ref(null);

// --- WATCHERS ---
// Clear errors immediately when a file is uploaded
watch(prfPhoto, (val) => { if (val) errors.value.prfPhoto = null; });
watch(nic, (val) => { if (val) errors.value.nic = null; });

const uploadPrfPhoto = async (event) => {
    const file = event.files[0];
    const url = await uploadFiles(file);
    if (url) {
        prfPhoto.value = url;
        notifyInfo('File uploaded successfully.');
    }
};

const uploadNIC = async (event) => {
    const file = event.files[0];
    const url = await uploadFiles(file);
    if (url) {
        nic.value = url;
        notifyInfo('File uploaded successfully.');
    }
};

const uploadLicense = async (event) => {
    const file = event.files[0];
    const url = await uploadFiles(file);
    if (url) {
        drivingLicence.value = url;
        notifyInfo('File uploaded successfully.');
    }
};

const uploadFiles = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
        const response = await uploadIdentification(formData);
        return response.data.url;
    } catch (error) {
        notifyError('File upload failed. Please try again.');
        return null;
    }
};

const validateForm = () => {
    errors.value = {};

    if (!prfPhoto.value) {
        errors.value.prfPhoto = "Professional photo upload is required";
    }
    if (!nic.value) {
        errors.value.nic = "NIC photo upload is required";
    }

    return Object.keys(errors.value).length === 0;
};

const Cancel = () => {
    emit("previous-step");
};

const Next = () => {
    if (!validateForm()) {
        return;
    }

    const payload = {
        professionalPhotoURL: prfPhoto.value,
        NICURL: nic.value,
        drivingLicenseURL: drivingLicence.value
    }
    emit("submit-verification-info", payload);
};
</script>

<style scoped>
.form {
    width: 100%;
    min-width: 60vw;
}

@media (max-width: 768px) {
    .form {
        min-width: none;
    }
}

/* Error text styling */
.error-text {
    color: #dc2626;
    font-size: 0.85rem;
    position: absolute;
    top: 100%;
    margin-top: 0.5rem;
}
</style>